import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash';
import { v4 } from 'uuid';

import { defaultSimplecastSetting } from 'utils/default/defaultSetting';
import {
  SimplecastIntegrationOption,
  SimplecastProgramFilter,
} from 'utils/models';

export const ensureDefaultSimplecastProgramFilters = (
  integration: SimplecastIntegrationOption,
): SimplecastIntegrationOption => {
  const defaults = defaultSimplecastSetting.filter;
  const programFilters = !isEmpty(integration.filter?.programFilters)
    ? integration.filter.programFilters
    : [...defaults.programFilters];

  return {
    ...integration,
    filter: {
      ...integration.filter,
      programFilters,
      isAutomatePushBack:
        integration.filter?.isAutomatePushBack ?? defaults.isAutomatePushBack,
      // Drop legacy hasChapters / isAutomatePushBack if present on stored prefs
      integrationSettings: {
        hasMonetisation:
          integration.filter?.integrationSettings?.hasMonetisation ??
          defaults.integrationSettings.hasMonetisation,
        hasTags:
          integration.filter?.integrationSettings?.hasTags ??
          defaults.integrationSettings.hasTags,
        hasSummary:
          integration.filter?.integrationSettings?.hasSummary ??
          defaults.integrationSettings.hasSummary,
      },
    },
  };
};

const simplecastSlice = createSlice({
  name: 'simplecast',
  initialState: {
    settings: [] as SimplecastIntegrationOption[],
  },
  reducers: {
    setSimplecastSettings: (
      state,
      { payload }: PayloadAction<SimplecastIntegrationOption[]>,
    ) => {
      state.settings = payload.map(ensureDefaultSimplecastProgramFilters);
    },
    updateSingleSimplecastIntegration: (
      state,
      { payload }: PayloadAction<SimplecastIntegrationOption>,
    ) => {
      const foundIndex = state.settings.findIndex(({ id }) => id === payload.id);

      if (foundIndex >= 0) {
        state.settings[foundIndex] = ensureDefaultSimplecastProgramFilters(payload);
      }
    },
    updateSimplecastProgramFilter: (
      state,
      {
        payload,
      }: PayloadAction<{
        integrationId: string;
        index: number;
        changes: Partial<SimplecastProgramFilter>;
      }>,
    ) => {
      const integration = state.settings.find(
        ({ id }) => id === payload.integrationId,
      );
      const programFilter = integration?.filter.programFilters[payload.index];

      if (integration && programFilter) {
        integration.filter.programFilters[payload.index] = {
          ...programFilter,
          ...payload.changes,
        };
      }
    },
    addSimplecastIntegration: (state) => {
      state.settings.push({ ...defaultSimplecastSetting, id: v4(), isEnabled: true });
    },
    addSimplecastIntegrationFilter: (
      state,
      { payload }: PayloadAction<SimplecastIntegrationOption>,
    ) => {
      const foundIndex = state.settings.findIndex(({ id }) => id === payload.id);

      if (foundIndex >= 0) {
        const newFilter = { ...state.settings[foundIndex].filter };

        newFilter.programFilters.push({
          programId: '',
          programName: '',
          rssFeedUrl: '',
        });

        state.settings[foundIndex].filter = newFilter;
      }
    },
    removeSimplecastIntegrationFilter: (
      state,
      {
        payload,
      }: PayloadAction<{
        integration: SimplecastIntegrationOption;
        index: number;
      }>,
    ) => {
      const { integration, index } = payload;

      const foundIndex = state.settings.findIndex(
        ({ id }) => id === integration.id,
      );

      if (foundIndex >= 0) {
        const newFilter = { ...state.settings[foundIndex].filter };

        if (newFilter.programFilters.length === 1) {
          newFilter.programFilters[0] = {
            programId: '',
            programName: '',
            rssFeedUrl: '',
          };

          state.settings[foundIndex].filter = newFilter;

          return;
        }

        newFilter.programFilters.splice(index, 1);
        state.settings[foundIndex].filter = newFilter;
      }
    },
    removeSimplecastIntegration: (
      state,
      { payload }: PayloadAction<{ integrationId: string }>,
    ) => {
      if (isEmpty(payload?.integrationId)) return;

      state.settings = state.settings.filter(
        (i) => i.id !== payload.integrationId,
      );
    },
  },
});

export const {
  setSimplecastSettings,
  updateSingleSimplecastIntegration,
  updateSimplecastProgramFilter,
  addSimplecastIntegration,
  addSimplecastIntegrationFilter,
  removeSimplecastIntegrationFilter,
  removeSimplecastIntegration,
} = simplecastSlice.actions;

export const simplecastReducer = simplecastSlice.reducer;
